/*
 * Copyright (c) 2025 Frayo and HomeChef360. All Rights Reserved.
 *
 * CONFIDENTIAL & PROPRIETARY
 * This software and its source code are the confidential and proprietary
 * information of Frayo and HomeChef360. Unauthorized copying, modification,
 * distribution, disclosure, or use of this code, in whole or in part, via any
 * medium, is strictly prohibited without prior written consent.
 *
 * Violations will be subject to legal action in the State of Florida and may
 * also be pursued under applicable U.S. federal law.
 */

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getDishView } from "../core/_requests";

const ViewDishes = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [dish, setDish] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDish = async () => {
      try {
        const res = await getDishView(id);
        setDish(res.data);
      } catch (error) {
        console.error("Error fetching dish details:", error);
        setError("Failed to fetch dish details.");
      } finally {
        setLoading(false);
      }
    };

    fetchDish();
  }, [id]);

  const editDish = () => {
    navigate("/dish/edit/" + id);
  };

  const deleteDish = () => {
    navigate("/dish/delete/" + id);
  };

  const clickCancel = () => {
    navigate("/dish");
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;

  return (
    <>
      <section className="container-fluid">
        <div className="row">
          <section className="content-section">
            <section className="row main-content settlement-content settings-content">
              <div className="title mb-3 d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  <h3 className="mb-0 me-2 fw-bold">
                    {dish?.name || "Dish Name"}
                  </h3>
                  <p className="mb-0 opacity-150 mt-2 d-flex align-items-center me-2">
                    <i
                      className="material-symbols-outlined info-ico me-1"
                      data-bs-toggle="tooltip"
                      data-bs-placement="top"
                      title={`${dish?.description || "Dish"}`}
                    >
                      info
                    </i>
                  </p>
                  <span className="badge ms-2" style={{ backgroundColor: dish?.dishType === 1 ? 'green' : 'red' }}>
                    {dish?.dishType === 1 ? 'Regular' : 'Bulk'}
                  </span>
                </div>


                <div className="d-flex align-items-center">
                  <div className="d-flex align-items-center">
                    <button
                      className="btn btn-primary btn-ico rounded-5 btn-empty"
                      onClick={clickCancel}
                    >
                      <i className="material-symbols-outlined">history</i>
                      <span>Back</span>
                    </button>

                  </div>
                  <span
                    className="text-primary cursor-pointer me-3 d-flex align-items-center"
                    onClick={editDish}
                  >
                    <i className="material-symbols-outlined me-1">edit</i>
                    Edit
                  </span>
                  <span
                    className="text-danger cursor-pointer d-flex align-items-center"
                    onClick={deleteDish}
                  >
                    <i className="material-symbols-outlined me-1">delete</i>
                    Delete
                  </span>

                </div>
              </div>
              <hr />
              <div className="col-12">
                <div className="row mb-4">
                  <div className="col-md-8">
                    <div className="snippet-block p-0">
                      <div className="cover-photo mb-3">
                        {dish?.dishImage ? (
                          <img src={dish?.dishImage} alt="Cover Photo" className="img-fluid rounded" />
                        ) : (
                          <img src="/assets/images/cover-placeholder.svg" alt="Cover Photo" className="img-fluid rounded" />
                        )}
                      </div>

                      <div className="cover-cont">
                        {dish?.dishvariants?.length > 0 && (
                          <div className="your-cuisine mb-4">
                            <div className="head-line">
                              <h6 className="fw-bold mb-3 mb-0">Dish Variants</h6>
                            </div>
                            <div className="variant-list mt-3">
                              {dish?.dishvariants.map((variant: any, index: any) => (
                                <div key={index} className="card shadow-sm p-3 mb-3 border-light">
                                  <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-center">
                                      <strong>Unit:</strong>
                                      <span className="status badge">
                                        {variant?.unit} {variant?.measurement?.name}
                                      </span>

                                      <strong>Price:</strong>
                                      <span className="status badge">
                                        ${variant?.price}
                                      </span>

                                      {/* Discount section */}
                                      {variant?.discount && (
                                        <div className="d-flex align-items-center">
                                          {/* Icon as a span element */}
                                          <span className="status badge bg-warning text-dark">
                                            {variant?.discount}% Off
                                          </span>
                                        </div>
                                      )}

                                      <strong>Final Price:</strong>
                                      <span className="status badge">${variant?.finalPrice}</span>
                                    </div>



                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}


                        {dish?.description && (
                          <div className="empty-certificate-wrap mb-3">
                            <div className="card shadow-sm p-3">
                              <div className="head-line">
                                <h6 className="fw-bold mb-1 text-center">Description</h6>
                              </div>
                              {dish?.description && <p className="mb-2">{dish?.description}</p>}
                            </div>
                          </div>
                        )}
                        {dish?.specialInstruction && (
                          <div className="card shadow-sm short-bio mb-3 p-3">
                            <div className="head-line">
                              <h6 className="fw-bold mb-1 text-center">Special Instruction:</h6>
                            </div>
                            <p>{dish?.specialInstruction}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="col-md-4">
                    {dish?.ingredientsDetails?.length > 0 && (
                      <div className="mb-4">
                        <div className="ingredient-block p-4 bg-light rounded shadow-sm">
                          <h6 className="fw-bold mb-3">Ingredients</h6>
                          <div className="d-flex flex-wrap gap-2">
                            {dish?.ingredientsDetails.map((ingredient: any, index: any) => (
                              <div
                                key={index}
                                className="ingredient-item p-2 border rounded-5 d-flex align-items-center"
                              >
                                <span className="me-2">
                                  <i className="bi bi-leaf"></i>
                                </span>
                                <span>{ingredient.name}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}


                    <div className="personal-detail mb-3">
                      <div className="details-block p-4 bg-light rounded shadow-sm">
                        <ul className="list-group list-group-flush">
                          {dish?.preparationTime && (
                            <li className="list-group-item d-flex align-items-center">
                              <i className="material-symbols-outlined me-2 text-primary">schedule</i>
                              <span className="fw-bold">Preparation Time:</span>
                              <span className="ms-2">{dish?.preparationTime}</span>
                            </li>
                          )}
                          {dish?.category?.name && (
                            <li className="list-group-item d-flex align-items-center">
                              <i className="material-symbols-outlined me-2 text-success">category</i>
                              <span className="fw-bold">Category:</span>
                              <span className="ms-2">{dish.category.name}</span>
                            </li>
                          )}
                          {dish?.cuisine?.name && (
                            <li className="list-group-item d-flex align-items-center">
                              <i className="material-symbols-outlined me-2 text-warning">restaurant_menu</i>
                              <span className="fw-bold">Cuisine:</span>
                              <span className="ms-2">{dish.cuisine.name}</span>
                            </li>
                          )}
                          {dish?.type?.name && (
                            <li className="list-group-item d-flex align-items-center">
                              <i className="material-symbols-outlined me-2 text-danger">fastfood</i>
                              <span className="fw-bold">Type:</span>
                              <span className="ms-2">{dish.type.name}</span>
                            </li>
                          )}
                        </ul>
                      </div>

                    </div>
                    {dish?.dish_addons?.length > 0 && (() => {
                      // Group addons by `addonstype_master.name`
                      const groupedAddons = dish?.dish_addons.reduce((acc: any, addon: any) => {
                        const groupName = addon?.addonstype_master?.name;
                        if (!acc[groupName]) {
                          acc[groupName] = [];
                        }
                        acc[groupName].push(addon?.addons_master?.name);
                        return acc;
                      }, {});

                      return (
                        <div className="mb-4">
                          <div className="addons-block p-4 rounded shadow-sm">
                            <h6 className="fw-bold mb-3">Add-ons</h6>
                            {Object.keys(groupedAddons).map((group, index) => (
                              <div key={index} className="mb-4">
                                <div className="card">
                                  <div className="card-header bg-blk text-white">
                                    <h6 className="mb-0">{group}</h6>
                                  </div>
                                  <div className="card-body">
                                    <ul className="list-unstyled">
                                      {groupedAddons[group]?.map((item: string, itemIndex: number) => (
                                        <li key={itemIndex} className="text-muted d-flex align-items-center mb-2">
                                          <span className="circle-icon me-2"></span>
                                          {item}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })()}


                  </div>
                </div>
              </div>

            </section>
          </section>
        </div>
      </section>
    </>
  );
};

export default ViewDishes;
